import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import React, { useEffect, useRef, useState } from 'react';
import AnnualBenefitForm, {
  AnnualBenefitFormRef,
  AnnualBenefitFormValues,
} from './AnnualBenefitForm';
import { useGetYTDValueByEmployeeId } from '@nxsys/hooks/api';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import AppLoader from '@nxsys/components/AppLoader';

type CreateAnnualBenefitProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (values: AnnualBenefitFormValues) => Promise<void>;
  employeeId: string;
};

const CreateAnnualBenefitModal: React.FC<CreateAnnualBenefitProps> = ({
  isOpen,
  toggle,
  onSubmit,
  employeeId,
}: CreateAnnualBenefitProps) => {
  const { messages } = useTypedIntl();
  const currentTaxWeekTaxYear = getTaxWeekTaxYear();

  const formRef = useRef<AnnualBenefitFormRef>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: AnnualBenefitFormValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit(values);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const {
    run: getYTDValueByEmployeeId,
    loading: isGetYTDValueByEmployeeIdLoading,
    value: ytdValueByEmployeeIdData,
  } = useGetYTDValueByEmployeeId();

  useEffect(() => {
    if (isOpen && employeeId) {
      getYTDValueByEmployeeId(employeeId, {
        taxYear: currentTaxWeekTaxYear.taxYear,
        taxWeek: currentTaxWeekTaxYear.taxWeek,
      });
    }
  }, [isOpen, employeeId]);

  useEffect(() => {
    if (ytdValueByEmployeeIdData) {
      formRef.current?.setFieldsValue({
        totalEeContribution:
          ytdValueByEmployeeIdData?.data?.annualBenefitTotalEeContribution?.toString() ||
          '0',
      });
    }
  }, [ytdValueByEmployeeIdData]);

  return (
    <AppModalContainer
      title={messages['common.addNewAnnualBenefit']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
    >
      {isGetYTDValueByEmployeeIdLoading && <AppLoader />}
      <AnnualBenefitForm formRef={formRef} onSubmit={handleSubmit} />
    </AppModalContainer>
  );
};

export default CreateAnnualBenefitModal;
